<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import InternalLayout from '@/layouts/InternalLayout.vue'
import AddressQuoteForm from '@/components/AddressQuoteForm.vue'
</script>

<template>
  <InternalLayout title="How It Works" hero-image="/img/manicured-home-lawn-walkway.jpg" hero-position="center 80%">
    <Head title="How It Works">
      <meta head-key="description" name="description" content="Learn how Mow Managers works. Enter your address, get a free instant price quote, and enjoy quality lawn care from trusted local professionals." />
      <link rel="canonical" href="https://mowmanagers.com/how-it-works" />
      <meta property="og:title" content="How It Works - Mow Managers" />
      <meta property="og:description" content="Enter your address, get a free instant price quote, and enjoy quality lawn care from trusted local professionals." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://mowmanagers.com/how-it-works" />
    </Head>

    <!-- ==================== FAIR PRICING INTRO ==================== -->
    <section aria-labelledby="pricing-heading" class="bg-white py-20 lg:py-28">
      <div class="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
        <p class="text-[21px] font-extrabold tracking-[4.2px] text-mm-green uppercase">
          Simple & Transparent
        </p>
        <h2 id="pricing-heading" class="mt-3 text-3xl font-bold text-mm-dark sm:text-4xl lg:text-[70px] lg:leading-[80px]">
          Fair Pricing For All,<br />Based on Actual Turf
        </h2>
        <p class="mx-auto mt-6 max-w-[720px] text-[20px] leading-[34px] font-medium text-[#6d706d]">
          When you enter your address and ZIP code on the pricing tab, our system will analyze your property and adjust the price based on your turf size. If you are satisfied with the results, you may proceed with submitting your order. We will review and you are good to go unless you hear from us.
        </p>
        <Link
          href="/get-started"
          class="mt-8 inline-block cursor-pointer rounded-md border-2 border-mm-green px-10 py-3 text-[16px] font-bold tracking-[1.6px] text-mm-green uppercase transition-colors hover:bg-mm-green hover:text-white"
        >
          Get My Price
        </Link>
      </div>
    </section>

    <!-- ==================== WE MAKE IT EASY — TWO COLUMN ==================== -->
    <section aria-labelledby="easy-heading" class="bg-mm-dark py-20 lg:py-28">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-24">
          <div>
            <p class="text-[21px] font-extrabold tracking-[4.2px] text-mm-green uppercase">
              Quick & Easy
            </p>
            <h2 id="easy-heading" class="mt-3 text-3xl font-bold text-white sm:text-4xl lg:text-[70px] lg:leading-[80px]">
              We Make<br />It Easy
            </h2>
            <p class="mt-6 max-w-[416px] text-[20px] leading-[34px] font-medium text-white">
              Just put in your address & zip code, that's all we
              need for you to customize your quote.
            </p>
            <div class="mt-6 flex items-start gap-3">
              <div class="w-[4px] shrink-0 self-stretch rounded-full bg-mm-green" aria-hidden="true" />
              <p class="text-[20px] leading-[34px] font-medium text-white">
                Get a free instant price quote<br />in just seconds.
              </p>
            </div>
          </div>

          <div class="rounded-[20px] bg-white p-8 lg:p-14">
            <h3 id="hiw-form-heading" class="text-center text-[26px] font-bold text-mm-dark lg:text-[30px]">
              Get a Free Instant<br />Price Quote.
            </h3>
            <div class="mt-8">
              <AddressQuoteForm variant="card" button-text="Submit" input-id-prefix="hiw" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== QUALITY SERVICE + IMAGE ==================== -->
    <section aria-labelledby="quality-heading" class="bg-white pt-20 pb-20 lg:pt-28 lg:pb-28">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div class="overflow-hidden rounded-[20px]">
            <img
              src="/img/below-header-middle.webp"
              alt="Family enjoying a freshly mowed lawn"
              class="h-full w-full object-cover"
              width="640"
              height="480"
              loading="lazy"
            />
          </div>

          <div>
            <p class="text-[21px] font-extrabold tracking-[4.2px] text-mm-green uppercase">
              The Budget Way to Mow Your Lawn
            </p>
            <h2 id="quality-heading" class="mt-3 text-3xl font-bold text-mm-dark sm:text-4xl lg:text-[70px] lg:leading-[88px]">
              Quality Service<br />and a Fair Price.
            </h2>
            <p class="mt-6 max-w-[628px] text-[20px] leading-[34px] font-medium text-[#6d706d]">
              Mow Managers connects you with trusted, local lawn
              care professionals at prices that won't break the
              bank. Get the quality you deserve with the
              convenience you need.
            </p>
            <Link
              href="/get-started"
              class="mt-8 inline-block cursor-pointer rounded-md bg-mm-green px-8 py-3 text-[16px] font-bold tracking-[1.6px] text-white uppercase transition-colors hover:bg-mm-green-dark"
            >
              Instant Quote
            </Link>
          </div>
        </div>
      </div>
    </section>
  </InternalLayout>
</template>
